'use client';

import type { Ref } from 'react';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { MentionsTextarea } from '@/app/components/shared/MentionsTextarea';
import type { BeforeAddMention, ElementModality, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import { cn } from '@/app/utils';
import { getHomeMentionsEditorMinHeightClass, type HomeGenerationMode, isHomeReferenceMode } from './utils';

interface HomeGenerationFormPromptInputProps {
  allowedModalities?: ElementModality[];
  className?: string;
  currentPlainPrompt: string;
  handleBeforeAddMention: BeforeAddMention;
  handleBeforeDeleteMention: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  inputRef?: Ref<HTMLTextAreaElement>;
  mode: HomeGenerationMode;
  promptMaxLength: number;
  onClear: () => void;
  onPromptChange: (value: string) => void;
  onSelectSubject: () => void;
  referencePrompts: ReferencePrompt[];
  referenceTextPrompt: TextPromptWithInputItem;
  promptPlaceholder: string;
  showClearButton: boolean;
  textareaMinHeight: number;
  fitContainer?: boolean;
  updateReferenceTextPrompt: (value: TextPromptWithInputItem) => void;
}

export const HomeGenerationFormPromptInput = ({
  allowedModalities,
  className,
  currentPlainPrompt,
  handleBeforeAddMention,
  handleBeforeDeleteMention,
  inputRef,
  mode,
  promptMaxLength,
  onClear,
  onPromptChange,
  onSelectSubject,
  referencePrompts,
  referenceTextPrompt,
  promptPlaceholder,
  showClearButton,
  textareaMinHeight,
  fitContainer,
  updateReferenceTextPrompt,
}: HomeGenerationFormPromptInputProps) =>
  isHomeReferenceMode(mode) ? (
    <MentionsTextarea
      className={cn(
        'max-h-[400px] text-sm [&_.ProseMirror]:text-base [&_.ProseMirror]:leading-6 lg:[&_.ProseMirror]:text-sm lg:[&_.ProseMirror]:leading-6',
        getHomeMentionsEditorMinHeightClass(textareaMinHeight),
        className,
      )}
      minHeight={textareaMinHeight}
      maxHeight={400}
      fitContainer={fitContainer}
      allowClear={showClearButton}
      onClear={onClear}
      textPrompt={referenceTextPrompt}
      updateTextPrompt={updateReferenceTextPrompt}
      onBeforeAddMention={handleBeforeAddMention}
      onBeforeDeleteMention={handleBeforeDeleteMention}
      getExtraItems={() => referencePrompts.filter((item) => item.status !== 'forbidden')}
      placeholder={promptPlaceholder}
      allowedModalities={allowedModalities}
      onAtButtonClick={onSelectSubject}
      maxLength={promptMaxLength}
    />
  ) : (
    <BasicTextarea
      minHeight={textareaMinHeight}
      maxHeight={400}
      fitContainer={fitContainer}
      className={cn('text-sm placeholder:text-system-white36 lg:text-sm', className)}
      ref={inputRef}
      placeholder={promptPlaceholder}
      textPrompt={currentPlainPrompt}
      updateTextPrompt={onPromptChange}
      showClearButton={showClearButton}
      onClearPrompt={onClear}
      clearButtonPlacement="inside"
      maxLength={promptMaxLength}
    />
  );
